import {
  calcularSerie,
  semanasDoMes,
  type Estudo,
  type Mes,
  type Perfil,
  type Sessao,
  type Visita,
} from '@sheafyield/dominio';
import {
  CONTA_LOCAL,
  baseDoRelatorioCompleta,
  idDoMes,
  idDoPerfil,
  type Banco,
} from '@sheafyield/sync';

import type { VisaoDoRelatorio } from './dados.js';

export interface RetratoDoRelatorio {
  visao: VisaoDoRelatorio;
  mesesPersistidos: Mes[];
  serieAutomatica: Mes[];
  alvoPersistido: Mes | undefined;
}

interface AnoMes {
  ano: number;
  mes: number;
}

function ordem({ ano, mes }: AnoMes): number {
  return ano * 12 + mes;
}

function menor(a: AnoMes, b: AnoMes): AnoMes {
  return ordem(a) <= ordem(b) ? a : b;
}

function maior(a: AnoMes, b: AnoMes): AnoMes {
  return ordem(a) >= ordem(b) ? a : b;
}

function mesLocal(iso: string): AnoMes {
  const data = new Date(iso);
  return { ano: data.getFullYear(), mes: data.getMonth() + 1 };
}

function pertenceAoContexto(linha: { user_id: string }, dono: string): boolean {
  return (
    linha.user_id === dono ||
    (dono !== CONTA_LOCAL && linha.user_id === CONTA_LOCAL)
  );
}

function maisNova<
  T extends { id: string; user_id: string; updated_at: string },
>(anterior: T | undefined, candidata: T, dono: string): T {
  if (anterior === undefined || candidata.updated_at > anterior.updated_at)
    return candidata;
  if (candidata.updated_at < anterior.updated_at) return anterior;
  // Durante a adoção pode existir por um instante a versão sentinela e a
  // versão já derivada. No empate, a identidade do dono atual é canônica.
  if (candidata.user_id === dono && anterior.user_id !== dono) return candidata;
  return candidata.id < anterior.id ? candidata : anterior;
}

function porIdDoContexto<
  T extends {
    id: string;
    user_id: string;
    updated_at: string;
  },
>(linhas: readonly T[], dono: string): T[] {
  const escolhidas = new Map<string, T>();
  for (const linha of linhas) {
    if (!pertenceAoContexto(linha, dono)) continue;
    escolhidas.set(linha.id, maisNova(escolhidas.get(linha.id), linha, dono));
  }
  return [...escolhidas.values()].sort((a, b) => a.id.localeCompare(b.id));
}

function mesesDoContexto(linhas: readonly Mes[], dono: string): Mes[] {
  const escolhidos = new Map<string, Mes>();
  for (const linha of linhas) {
    if (!pertenceAoContexto(linha, dono)) continue;
    const chave = `${String(linha.ano)}-${String(linha.mes)}`;
    escolhidos.set(chave, maisNova(escolhidos.get(chave), linha, dono));
  }
  return [...escolhidos.values()].sort((a, b) => ordem(a) - ordem(b));
}

function perfilVirtual(dono: string, agora: Date): Perfil {
  return {
    id: idDoPerfil(dono),
    user_id: dono,
    updated_at: agora.toISOString(),
    server_updated_at: null,
    deleted: false,
    tipo: 'publicador',
    meta_horas_mes: 0,
    nome_exibicao: '',
  };
}

function primeiroMesRelevante(
  alvo: AnoMes,
  sessoes: readonly Sessao[],
  estudos: readonly Estudo[],
  visitas: readonly Visita[],
  meses: readonly Mes[],
): AnoMes {
  let inicio = alvo;
  for (const sessao of sessoes)
    if (!sessao.deleted) inicio = menor(inicio, mesLocal(sessao.inicio));
  for (const estudo of estudos)
    if (!estudo.deleted) inicio = menor(inicio, mesLocal(estudo.iniciado_em));
  for (const visita of visitas)
    if (!visita.deleted) inicio = menor(inicio, mesLocal(visita.em));
  for (const item of meses) if (!item.deleted) inicio = menor(inicio, item);
  return inicio;
}

function ultimoMesNecessario(alvo: AnoMes, meses: readonly Mes[]): AnoMes {
  return meses.reduce(
    (fim, item) => (item.deleted ? fim : maior(fim, item)),
    alvo,
  );
}

export function conteudoDoMes(
  item: Mes,
): Omit<Mes, 'updated_at' | 'server_updated_at'> {
  const { updated_at, server_updated_at, ...conteudo } = item;
  void updated_at;
  void server_updated_at;
  return conteudo;
}

function conteudoDoPerfil(
  item: Perfil,
): Omit<Perfil, 'updated_at' | 'server_updated_at'> {
  const { updated_at, server_updated_at, ...conteudo } = item;
  void updated_at;
  void server_updated_at;
  return conteudo;
}

function ordenarChaves(valor: unknown): unknown {
  if (Array.isArray(valor)) return valor.map(ordenarChaves);
  if (valor === null || typeof valor !== 'object') return valor;
  return Object.fromEntries(
    Object.entries(valor)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([chave, conteudo]) => [chave, ordenarChaves(conteudo)]),
  );
}

function revisaoDe(valor: unknown): string {
  return JSON.stringify(ordenarChaves(valor));
}

export function mesmoConteudo(a: Mes, b: Mes): boolean {
  return revisaoDe(conteudoDoMes(a)) === revisaoDe(conteudoDoMes(b));
}

export function validarAgora(agora: Date): void {
  if (!Number.isFinite(agora.getTime()))
    throw new RangeError('O instante do comando é inválido.');
}

export function entradaDoMes(
  item: Mes,
): Omit<Mes, 'updated_at' | 'server_updated_at'> {
  return conteudoDoMes(item);
}

function semApuracaoParcial(item: Mes): Mes {
  return {
    ...item,
    participou: false,
    estudos: 0,
    horas_minutos: 0,
    creditos_minutos: 0,
    transporte_minutos: 0,
  };
}

export async function retratoDentroDaTransacao(
  banco: Banco,
  dono: string,
  ano: number,
  mes: number,
  agora: Date,
): Promise<RetratoDoRelatorio> {
  validarAgora(agora);
  const [perfisLidos, sessoesLidas, estudosLidos, visitasLidas, mesesLidos] =
    await Promise.all([
      banco.perfil.toArray(),
      banco.sessao.toArray(),
      banco.estudo.toArray(),
      banco.visita.toArray(),
      banco.mes.toArray(),
    ]);
  const perfis = porIdDoContexto(perfisLidos, dono);
  // Lápide participa da eleição LWW. Filtrá-la antes ressuscitaria um perfil
  // sentinela vivo, porém mais antigo; no empate, `maisNova` já dá a vitória
  // à identidade canônica do dono atual. Só depois da eleição uma vencedora
  // apagada vira ausência e dá lugar ao perfil virtual.
  const perfilVencedor = perfis.reduce<Perfil | undefined>(
    (escolhido, item) => maisNova(escolhido, item, dono),
    undefined,
  );
  const perfilPersistido = perfilVencedor?.deleted ? undefined : perfilVencedor;
  const perfil = perfilPersistido ?? perfilVirtual(dono, agora);
  const sessoes = porIdDoContexto(sessoesLidas, dono);
  const estudos = porIdDoContexto(estudosLidos, dono);
  const visitas = porIdDoContexto(visitasLidas, dono);
  const mesesPersistidos = mesesDoContexto(mesesLidos, dono).filter(
    (item) => !item.deleted,
  );
  const alvo = { ano, mes };
  const inicio = primeiroMesRelevante(
    alvo,
    sessoes,
    estudos,
    visitas,
    mesesPersistidos,
  );
  const fim = ultimoMesNecessario(alvo, mesesPersistidos);
  const entradaComum = {
    perfil,
    sessoes,
    estudos,
    visitas,
    inicio,
    fim,
    transporteInicial: 0,
    id: (a: number, m: number) => idDoMes(dono, a, m),
    agora,
  };
  const serieCalculada = calcularSerie({
    ...entradaComum,
    existentes: mesesPersistidos,
  });
  const existentesAutomaticos = mesesPersistidos.map((item) =>
    item.ano === ano && item.mes === mes
      ? { ...item, sobrescrito: false }
      : item,
  );
  const serieAutomatica = calcularSerie({
    ...entradaComum,
    existentes: existentesAutomaticos,
  });
  const calculado = serieCalculada.find(
    (item) => item.ano === ano && item.mes === mes,
  );
  const automatico = serieAutomatica.find(
    (item) => item.ano === ano && item.mes === mes,
  );
  if (calculado === undefined || automatico === undefined)
    throw new Error('O mês solicitado não foi produzido pela apuração.');
  const alvoPersistido = mesesPersistidos.find(
    (item) => item.ano === ano && item.mes === mes,
  );
  const baseCompleta = await baseDoRelatorioCompleta(banco, dono);
  // Durante um pull incompleto, os registros que já chegaram são só uma
  // fração da verdade. A prévia automática continua disponível para o aviso
  // e a comparação, mas nunca substitui um mês íntegro já persistido.
  const mesesVisiveis = baseCompleta
    ? serieCalculada
    : serieCalculada.map((item) => {
        const persistido = mesesPersistidos.find(
          (candidato) =>
            candidato.ano === item.ano && candidato.mes === item.mes,
        );
        // Sem exemplar íntegro não existe total seguro para mostrar. O
        // automático parcial fica separado no contrato, acompanhado de
        // `baseCompleta: false`, e nunca se disfarça de mês corrente.
        return persistido ?? semApuracaoParcial(item);
      });
  const atual =
    mesesVisiveis.find((item) => item.ano === ano && item.mes === mes) ??
    calculado;
  // A série semanal não possui versão persistida à qual recorrer. Enquanto
  // faltam linhas do pull, omiti-la é a única forma de não desenhar um
  // gráfico parcial com aparência de total fechado.
  const semanas = baseCompleta ? semanasDoMes(sessoes, ano, mes, agora) : [];
  const revisao = revisaoDe({
    dono,
    alvo,
    baseCompleta,
    perfilReal: perfilVencedor ?? null,
    perfilCalculado: conteudoDoPerfil(perfil),
    sessoes,
    estudos,
    visitas,
    mesesReais: mesesPersistidos,
    mesesCalculados: serieCalculada.map(conteudoDoMes),
    automatico: conteudoDoMes(automatico),
  });
  return {
    visao: {
      perfil,
      perfilPersistido: perfilPersistido !== undefined,
      atual,
      automatico,
      meses: mesesVisiveis,
      semanas,
      baseCompleta,
      revisao,
    },
    mesesPersistidos,
    serieAutomatica,
    alvoPersistido,
  };
}

export function lerRelatorio(
  banco: Banco,
  dono: string,
  ano: number,
  mes: number,
  agora: Date,
): Promise<VisaoDoRelatorio> {
  return banco.transaction(
    'r',
    [
      banco.perfil,
      banco.sessao,
      banco.estudo,
      banco.visita,
      banco.mes,
      banco.estado,
    ],
    async () =>
      (await retratoDentroDaTransacao(banco, dono, ano, mes, agora)).visao,
  );
}
